import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import type {
  IncidentDto,
  InvoiceDto,
  ReservationDto,
  SessionDto,
} from '@parking/shared';
import { api } from '../shared/api/client';
import { useRealtime } from '../shared/realtime/useRealtime';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { Card } from '../shared/ui/Card';
import { StatTile } from '../shared/ui/StatTile';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

/** Mount ApexCharts only when the container has a real width (avoids blank donuts on first login). */
function DeferredChart({
  ready,
  chartKey,
  type,
  series,
  options,
  height = 280,
}: {
  ready: boolean;
  chartKey: string;
  type: 'donut' | 'bar' | 'area' | 'line';
  series: number[] | Array<{ name?: string; data: number[] }>;
  options: ApexOptions;
  height?: number;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [sized, setSized] = useState(false);

  useEffect(() => {
    if (!ready) {
      setSized(false);
      return;
    }
    const el = hostRef.current;
    if (!el) return;

    const check = () => {
      if (el.clientWidth > 0) setSized(true);
    };
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    const t = window.setTimeout(check, 50);
    return () => {
      ro?.disconnect();
      window.clearTimeout(t);
    };
  }, [ready, chartKey]);

  return (
    <div ref={hostRef} className="dashboard-chart__host">
      {ready && sized ? (
        <Chart
          key={chartKey}
          type={type}
          height={height}
          width="100%"
          series={series}
          options={options}
        />
      ) : (
        <div className="dashboard-chart__placeholder text-muted">…</div>
      )}
    </div>
  );
}

function money(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function moneyExact(cents: number, currency = 'EUR'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

function countBy<T>(items: T[], key: (item: T) => string): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const k = key(item);
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
}

function lastNDaysLabels(n: number): string[] {
  const labels: string[] = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(now.getDate() - i);
    labels.push(
      d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' }),
    );
  }
  return labels;
}

function invoiceCentsByDay(invoices: InvoiceDto[], days: number): number[] {
  const now = new Date();
  const buckets = Array.from({ length: days }, () => 0);
  for (const inv of invoices) {
    if (inv.status === 'VOID') continue;
    const when = inv.paidAt ?? inv.issuedAt;
    if (!when) continue;
    const d = new Date(when);
    const diff = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
    if (diff >= 0 && diff < days) {
      const idx = days - 1 - diff;
      buckets[idx] = (buckets[idx] ?? 0) + inv.totalCents;
    }
  }
  return buckets.map((c) => Math.round(c) / 100);
}

const chartBase: ApexOptions = {
  chart: {
    toolbar: { show: false },
    fontFamily: 'Poppins, sans-serif',
    animations: { enabled: true },
    redrawOnParentResize: true,
    redrawOnWindowResize: true,
  },
  legend: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
  },
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: 3 },
  grid: {
    borderColor: '#eff2f5',
    strokeDashArray: 4,
  },
};

export function DashboardPage() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const canReports = useAuthStore((s) => s.hasPermission('reports:read'));
  const canSessions = useAuthStore((s) => s.hasPermission('sessions:read'));
  const canBilling = useAuthStore((s) => s.hasPermission('billing:read'));
  const canReservations = useAuthStore((s) => s.hasPermission('reservations:read'));
  const canIncidents = useAuthStore((s) => s.hasPermission('incidents:read'));
  const canParkings = useAuthStore((s) => s.hasPermission('parkings:read'));
  const { connected, lastOccupancy, lastIncidentAt } = useRealtime();

  const occupancy = useQuery({
    queryKey: ['dashboard', 'occupancy', lastOccupancy?.sensorId, lastOccupancy?.isOccupied],
    queryFn: async () => (await api.occupancyReport()).data,
    refetchInterval: 15_000,
    enabled: Boolean(user) && canReports,
  });

  const revenue = useQuery({
    queryKey: ['dashboard', 'revenue'],
    queryFn: async () => (await api.revenueReport()).data,
    refetchInterval: 60_000,
    enabled: Boolean(user) && canReports,
  });

  const sessions = useQuery({
    queryKey: ['dashboard', 'sessions-open'],
    queryFn: async () => (await api.listOpenSessions()).data,
    refetchInterval: 20_000,
    enabled: Boolean(user) && canSessions,
  });

  const invoices = useQuery({
    queryKey: ['dashboard', 'invoices'],
    queryFn: async () => (await api.listInvoices()).data,
    refetchInterval: 60_000,
    enabled: Boolean(user) && canBilling,
  });

  const reservations = useQuery({
    queryKey: ['dashboard', 'reservations'],
    queryFn: async () => (await api.listReservations()).data,
    refetchInterval: 45_000,
    enabled: Boolean(user) && canReservations,
  });

  const incidents = useQuery({
    queryKey: ['dashboard', 'incidents'],
    queryFn: async () => (await api.listIncidents()).data,
    refetchInterval: 45_000,
    enabled: Boolean(user) && canIncidents,
  });

  const parkings = useQuery({
    queryKey: ['dashboard', 'parkings'],
    queryFn: async () => (await api.listParkings()).data,
    refetchInterval: 60_000,
    enabled: Boolean(user) && canParkings,
  });

  const occ = occupancy.data;
  const rev = revenue.data;
  const sessionRows: SessionDto[] = sessions.data ?? [];
  const invoiceRows: InvoiceDto[] = invoices.data ?? [];
  const reservationRows: ReservationDto[] = reservations.data ?? [];
  const incidentRows: IncidentDto[] = incidents.data ?? [];

  const openReservations = reservationRows.filter((r) =>
    ['PENDING', 'CONFIRMED', 'CHECKED_IN'].includes(r.status),
  ).length;
  const openIncidents = incidentRows.filter((r) =>
    ['OPEN', 'IN_PROGRESS'].includes(r.status),
  ).length;
  const paidInvoices = invoiceRows.filter((i) => i.status === 'PAID').length;
  const issuedInvoices = invoiceRows.filter((i) => i.status === 'ISSUED').length;

  const occupancyDonut = useMemo(() => {
    const occupied = occ?.occupiedSpots ?? 0;
    const free = occ?.availableSpots ?? 0;
    const other = Math.max(0, (occ?.totalSpots ?? 0) - occupied - free);
    return {
      series: [occupied, free, other],
      options: {
        ...chartBase,
        chart: { ...chartBase.chart, type: 'donut' as const },
        labels: [
          t('dashboard.charts.occupied'),
          t('dashboard.charts.available'),
          t('dashboard.charts.other'),
        ],
        colors: ['#f1416c', '#50cd89', '#A1A5B7'],
        plotOptions: {
          pie: {
            donut: {
              size: '72%',
              labels: {
                show: true,
                name: { show: true, fontSize: '13px', color: '#A1A5B7' },
                value: {
                  show: true,
                  fontSize: '22px',
                  fontWeight: 700,
                  color: '#181C32',
                },
                total: {
                  show: true,
                  label: t('dashboard.cards.occupancy'),
                  fontSize: '12px',
                  color: '#A1A5B7',
                  formatter: () =>
                    occ != null ? `${Math.round(occ.occupancyRate * 100)}%` : '—',
                },
              },
            },
          },
        },
      } satisfies ApexOptions,
    };
  }, [occ, t]);

  const reservationChart = useMemo(() => {
    const counts = countBy(reservationRows, (r) => r.status);
    const labels = Object.keys(counts);
    const series = labels.map((l) => counts[l] ?? 0);
    return {
      series: series.length ? series : [0],
      options: {
        ...chartBase,
        chart: { ...chartBase.chart, type: 'donut' as const },
        labels: labels.length
          ? labels.map((l) => enumLabel(t, 'reservationStatus', l))
          : [t('dashboard.charts.empty')],
        colors: ['#009ef7', '#50cd89', '#ffc700', '#f1416c', '#7239ea', '#A1A5B7'],
        plotOptions: {
          pie: { donut: { size: '68%' } },
        },
      } satisfies ApexOptions,
    };
  }, [reservationRows, t]);

  const revenueTrend = useMemo(() => {
    const days = 7;
    const labels = lastNDaysLabels(days);
    const data = invoiceCentsByDay(invoiceRows, days);
    return {
      series: [{ name: t('dashboard.charts.billing'), data }],
      options: {
        ...chartBase,
        chart: { ...chartBase.chart, type: 'area' as const, height: 280 },
        colors: ['#009ef7'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.35,
            opacityTo: 0.05,
            stops: [0, 90, 100],
          },
        },
        xaxis: {
          categories: labels,
          labels: { style: { colors: '#A1A5B7', fontSize: '11px' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#A1A5B7', fontSize: '11px' },
            formatter: (v: number) => `${v.toFixed(0)}`,
          },
        },
        tooltip: {
          y: {
            formatter: (v: number) =>
              moneyExact(Math.round(v * 100), rev?.currency ?? 'EUR'),
          },
        },
      } satisfies ApexOptions,
    };
  }, [invoiceRows, rev?.currency, t]);

  const invoiceStatusChart = useMemo(() => {
    const counts = countBy(invoiceRows, (i) => i.status);
    const order = ['PAID', 'ISSUED', 'DRAFT', 'VOID', 'RECTIFIED'];
    const labels = order.filter((k) => (counts[k] ?? 0) > 0);
    const series = [
      {
        name: t('dashboard.charts.invoices'),
        data: labels.map((l) => counts[l] ?? 0),
      },
    ];
    return {
      series: labels.length ? series : [{ name: t('dashboard.charts.invoices'), data: [0] }],
      options: {
        ...chartBase,
        chart: { ...chartBase.chart, type: 'bar' as const, height: 280 },
        plotOptions: {
          bar: { borderRadius: 6, columnWidth: '45%', distributed: true },
        },
        colors: ['#50cd89', '#009ef7', '#A1A5B7', '#f1416c', '#ffc700'],
        legend: { show: false },
        xaxis: {
          categories: labels.length
            ? labels.map((l) => enumLabel(t, 'invoiceStatus', l))
            : [t('dashboard.charts.empty')],
          labels: { style: { colors: '#A1A5B7', fontSize: '11px' } },
        },
        yaxis: {
          labels: { style: { colors: '#A1A5B7', fontSize: '11px' } },
        },
      } satisfies ApexOptions,
    };
  }, [invoiceRows, t]);

  const recentSessions = sessionRows.slice(0, 6);
  const recentInvoices = [...invoiceRows]
    .sort((a, b) => +new Date(b.issuedAt ?? 0) - +new Date(a.issuedAt ?? 0))
    .slice(0, 6);
  const upcomingReservations = [...reservationRows]
    .filter((r) => ['PENDING', 'CONFIRMED', 'CHECKED_IN'].includes(r.status))
    .sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt))
    .slice(0, 6);
  const openIncidentRows = incidentRows
    .filter((r) => ['OPEN', 'IN_PROGRESS'].includes(r.status))
    .slice(0, 6);

  const parkingRows = parkings.data ?? [];

  return (
    <div className="dashboard">
      <div className="dashboard__intro mb-4">
        <div>
          <h2 className="dashboard__title">{t('dashboard.title')}</h2>
          <p className="dashboard__subtitle mb-0">{t('dashboard.subtitle')}</p>
        </div>
        <div className={`dashboard__live ${connected ? 'is-on' : 'is-off'}`}>
          <span className="dashboard__live-dot" />
          {connected ? t('dashboard.liveOn') : t('dashboard.liveOff')}
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="danger"
            icon="bi-pie-chart"
            label={t('dashboard.cards.occupancy')}
            value={occ != null ? `${Math.round(occ.occupancyRate * 100)}%` : '—'}
            hint={
              lastOccupancy
                ? `${lastOccupancy.isOccupied ? 'OCC' : 'FREE'} · ${(lastOccupancy.spotId ?? lastOccupancy.barrierId ?? lastOccupancy.sensorId).slice(0, 8)}`
                : undefined
            }
          />
        </div>
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="warning"
            icon="bi-p-circle"
            label={t('dashboard.cards.spots')}
            value={occ != null ? `${occ.occupiedSpots}/${occ.totalSpots}` : '—'}
            hint={t('dashboard.cards.spotsHint')}
          />
        </div>
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="primary"
            icon="bi-ticket-perforated"
            label={t('dashboard.cards.vehicles')}
            value={occ != null ? String(occ.openSessions) : String(sessionRows.length || '—')}
            hint={t('dashboard.cards.sessionsHint')}
          />
        </div>
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="success"
            icon="bi-check2-circle"
            label={t('dashboard.cards.available')}
            value={occ != null ? String(occ.availableSpots) : '—'}
          />
        </div>
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="info"
            icon="bi-calendar-check"
            label={t('dashboard.cards.reservations')}
            value={canReservations ? String(openReservations) : '—'}
            hint={t('dashboard.cards.reservationsHint')}
          />
        </div>
        <div className="col-6 col-md-4 col-xl-2">
          <StatTile
            tone="dark"
            icon="bi-currency-euro"
            label={t('dashboard.cards.revenue')}
            value={rev != null ? money(rev.paidCents, rev.currency) : '—'}
            hint={
              rev != null
                ? t('dashboard.cards.revenueHint', { count: rev.invoiceCount })
                : undefined
            }
          />
        </div>
      </div>

      <div className="row g-4 mb-4">
        {canReports && (
          <div className="col-lg-4">
            <Card title={t('dashboard.charts.occupancyTitle')} className="h-100">
              <div className="dashboard-chart">
                <DeferredChart
                  ready={occupancy.isSuccess && Boolean(occ)}
                  chartKey={`occ-${occupancyDonut.series.join('-')}-${Math.round((occ?.occupancyRate ?? 0) * 100)}`}
                  type="donut"
                  series={occupancyDonut.series}
                  options={occupancyDonut.options}
                />
              </div>
            </Card>
          </div>
        )}
        {canReservations && (
          <div className="col-lg-4">
            <Card
              title={t('dashboard.charts.reservationsTitle')}
              className="h-100"
              toolbar={
                <Link to="/reservations" className="btn btn-sm btn-light">
                  {t('dashboard.viewAll')}
                </Link>
              }
            >
              <div className="dashboard-chart">
                <DeferredChart
                  ready={reservations.isSuccess}
                  chartKey={`res-${(reservationChart.options.labels as string[] | undefined)?.join('|') ?? ''}-${reservationChart.series.join('-')}`}
                  type="donut"
                  series={reservationChart.series}
                  options={reservationChart.options}
                />
              </div>
            </Card>
          </div>
        )}
        {canBilling && (
          <div className="col-lg-4">
            <Card
              title={t('dashboard.charts.invoicesTitle')}
              className="h-100"
              toolbar={
                <span className="text-muted small">
                  {t('dashboard.cards.invoiceStats', {
                    paid: paidInvoices,
                    issued: issuedInvoices,
                  })}
                </span>
              }
            >
              <div className="dashboard-chart">
                <DeferredChart
                  ready={invoices.isSuccess}
                  chartKey={`inv-${(invoiceStatusChart.series[0]?.data as number[] | undefined)?.join('-') ?? '0'}`}
                  type="bar"
                  series={invoiceStatusChart.series}
                  options={invoiceStatusChart.options}
                />
              </div>
            </Card>
          </div>
        )}
        {!canReports && !canReservations && !canBilling && (
          <div className="col-12">
            <div className="alert alert-light border mb-0">{t('dashboard.noCharts')}</div>
          </div>
        )}
      </div>

      {canBilling && (
        <div className="row g-4 mb-4">
          <div className="col-12">
            <Card
              title={t('dashboard.charts.revenueTitle')}
              toolbar={
                rev ? (
                  <span className="text-muted small">
                    {t('dashboard.cards.collected')}:{' '}
                    <strong>{moneyExact(rev.paidCents, rev.currency)}</strong>
                    {' · '}
                    {t('dashboard.cards.invoiced')}:{' '}
                    <strong>{moneyExact(rev.totalCents, rev.currency)}</strong>
                  </span>
                ) : undefined
              }
            >
              <div className="dashboard-chart dashboard-chart--wide">
                <DeferredChart
                  ready={invoices.isSuccess}
                  chartKey={`rev-${revenueTrend.series[0]?.data?.join('-') ?? '0'}`}
                  type="area"
                  height={280}
                  series={revenueTrend.series}
                  options={revenueTrend.options}
                />
              </div>
            </Card>
          </div>
        </div>
      )}

      <div className="row g-4 mb-4">
        {canSessions && (
          <div className="col-lg-6">
            <Card
              title={t('dashboard.lists.openSessions')}
              className="h-100"
              toolbar={
                <Link to="/sessions" className="btn btn-sm btn-light">
                  {t('dashboard.viewAll')}
                </Link>
              }
            >
              {recentSessions.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.lists.empty')}</p>
              ) : (
                <ul className="dashboard-list">
                  {recentSessions.map((s) => (
                    <li key={s.id}>
                      <div>
                        <div className="fw-semibold">{s.plate}</div>
                        <div className="text-muted small">
                          {s.spotCode ?? '—'} · {new Date(s.entryAt).toLocaleString()}
                        </div>
                      </div>
                      <StatusBadge tone={statusToneFromEnum(s.status)}>
                        {enumLabel(t, 'sessionStatus', s.status)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {canBilling && (
          <div className="col-lg-6">
            <Card
              title={t('dashboard.lists.recentInvoices')}
              className="h-100"
              toolbar={
                <Link to="/invoices" className="btn btn-sm btn-light">
                  {t('dashboard.viewAll')}
                </Link>
              }
            >
              {recentInvoices.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.lists.empty')}</p>
              ) : (
                <ul className="dashboard-list">
                  {recentInvoices.map((inv) => (
                    <li key={inv.id}>
                      <div>
                        <div className="fw-semibold">
                          {inv.fullNumber || String(inv.number)}
                        </div>
                        <div className="text-muted small">
                          {moneyExact(inv.totalCents, inv.currency)}
                          {inv.issuedAt
                            ? ` · ${new Date(inv.issuedAt).toLocaleDateString()}`
                            : ''}
                        </div>
                      </div>
                      <StatusBadge tone={statusToneFromEnum(inv.status)}>
                        {enumLabel(t, 'invoiceStatus', inv.status)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {canReservations && (
          <div className="col-lg-6">
            <Card
              title={t('dashboard.lists.reservations')}
              className="h-100"
              toolbar={
                <Link to="/reservations" className="btn btn-sm btn-light">
                  {t('dashboard.viewAll')}
                </Link>
              }
            >
              {upcomingReservations.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.lists.empty')}</p>
              ) : (
                <ul className="dashboard-list">
                  {upcomingReservations.map((r) => (
                    <li key={r.id}>
                      <div>
                        <div className="fw-semibold">
                          {r.qrCode ?? r.id.slice(0, 8)}
                        </div>
                        <div className="text-muted small">
                          {new Date(r.startsAt).toLocaleString()}
                          {r.spotId ? ` · ${r.spotId.slice(0, 8)}` : ''}
                        </div>
                      </div>
                      <StatusBadge tone={statusToneFromEnum(r.status)}>
                        {enumLabel(t, 'reservationStatus', r.status)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}

        {canIncidents && (
          <div className="col-lg-6">
            <Card
              title={t('dashboard.lists.incidents')}
              className="h-100"
              toolbar={
                <div className="d-flex align-items-center gap-2">
                  {lastIncidentAt && (
                    <span className="text-muted small">
                      {t('dashboard.lists.lastIncident')}:{' '}
                      {new Date(lastIncidentAt).toLocaleTimeString()}
                    </span>
                  )}
                  <Link to="/incidents" className="btn btn-sm btn-light">
                    {t('dashboard.viewAll')}
                  </Link>
                </div>
              }
            >
              {openIncidentRows.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.lists.emptyIncidents')}</p>
              ) : (
                <ul className="dashboard-list">
                  {openIncidentRows.map((inc) => (
                    <li key={inc.id}>
                      <div>
                        <div className="fw-semibold">{inc.title}</div>
                        <div className="text-muted small">
                          {enumLabel(t, 'incidentType', inc.type)} ·{' '}
                          {new Date(inc.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <StatusBadge tone={statusToneFromEnum(inc.status)}>
                        {enumLabel(t, 'incidentStatus', inc.status)}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        )}
      </div>

      <div className="row g-4">
        {canParkings && (
          <div className="col-lg-8">
            <Card title={t('dashboard.lists.parkings')}>
              {parkingRows.length === 0 ? (
                <p className="text-muted small mb-0">{t('dashboard.lists.empty')}</p>
              ) : (
                <div className="row g-3">
                  {parkingRows.slice(0, 8).map((p) => (
                    <div key={p.id} className="col-sm-6 col-xl-3">
                      <div className="dashboard-parking-chip">
                        <div className="fw-semibold">{p.code}</div>
                        <div className="text-muted small text-truncate">{p.name}</div>
                        <div className="dashboard-parking-chip__meta">
                          <span>
                            {p.spotsAvailable ?? '—'} {t('dashboard.charts.available').toLowerCase()}
                          </span>
                          <span>{p.occupancyPct ?? 0}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
        <div className={canParkings ? 'col-lg-4' : 'col-12'}>
          <Card title={t('dashboard.live.title')}>
            <div className="d-flex align-items-start gap-3">
              <span className={`symbol ${connected ? 'symbol-success' : 'symbol-secondary'}`}>
                <i className={`bi ${connected ? 'bi-wifi' : 'bi-wifi-off'}`} />
              </span>
              <div>
                <div className="fw-semibold">Socket.IO</div>
                <div className="text-muted small">
                  {connected ? t('dashboard.live.connected') : t('dashboard.live.waiting')}
                </div>
                {openIncidents > 0 && (
                  <div className="mt-2 small text-danger">
                    {t('dashboard.live.openIncidents', { count: openIncidents })}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
